import { useEffect, useMemo, useState } from "react";
import apiClient from "../utils/apiClient";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Paper,
  Radio,
  RadioGroup,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Send as SendIcon, NotificationsActive } from "@mui/icons-material";
import PageLayout from "../components/admin/PageLayout";

const QUICK_TEMPLATES = [
  {
    key: "maintenance",
    label: "Maintenance alert",
    title: "Scheduled maintenance",
    body: "Treasure services will be under maintenance tonight from 11:00 PM to 12:00 AM.",
    data: { type: "admin_maintenance" },
  },
  {
    key: "reminder",
    label: "Expense reminder",
    title: "Reminder: update your expenses",
    body: "Please add today's expenses so your reports and balances stay accurate.",
    data: { type: "admin_reminder", screen: "transaction" },
  },
  {
    key: "new-feature",
    label: "New feature",
    title: "New update available",
    body: "We launched improvements in expense sharing and reports. Open app to explore.",
    data: { type: "admin_update", screen: "dashboard" },
  },
  {
    key: "security",
    label: "Security notice",
    title: "Keep your account secure",
    body: "Never share your OTP or password. Treasure will never ask for them over chat or phone.",
    data: { type: "admin_security" },
  },
  {
    key: "support",
    label: "Support / help",
    title: "Need help?",
    body: "Visit Settings or our website for FAQs and support. We are here if something looks wrong.",
    data: { type: "admin_support", screen: "settings" },
  },
  {
    key: "weekly-summary",
    label: "Weekly summary",
    title: "Your week in Treasure",
    body: "Review this week's income, expenses, and shared groups. Open the app for your full summary.",
    data: { type: "admin_weekly", screen: "dashboard" },
  },
  {
    key: "promo",
    label: "Gentle promo",
    title: "Thanks for using Treasure",
    body: "Share Treasure with friends who track expenses together. Your feedback helps us improve.",
    data: { type: "admin_promo" },
  },
];

const Notifications = () => {
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [sending, setSending] = useState(false);
  const [users, setUsers] = useState([]);
  const [targetType, setTargetType] = useState("single");
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [extraData, setExtraData] = useState("");
  const [result, setResult] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, severity: "success", message: "" });
  const [activeTemplate, setActiveTemplate] = useState("");

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const userOptions = useMemo(
    () =>
      users.map((u) => ({
        id: u.id,
        label: `${u.name || "Unnamed"}${u.email ? ` (${u.email})` : ""}`,
      })),
    [users]
  );

  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await apiClient.get("users");
      setUsers(response?.data?.data || []);
    } catch (error) {
      setSnackbar({ open: true, severity: "error", message: "Failed to load users list." });
    } finally {
      setLoadingUsers(false);
    }
  };

  const parseData = () => {
    const raw = extraData.trim();
    if (!raw) return {};
    try {
      const parsed = JSON.parse(raw);
      if (!parsed || Array.isArray(parsed) || typeof parsed !== "object") {
        throw new Error("Invalid data JSON object");
      }
      return parsed;
    } catch (e) {
      throw new Error("Data must be valid JSON object");
    }
  };

  const handleSend = async () => {
    try {
      if (!title.trim()) {
        setSnackbar({ open: true, severity: "warning", message: "Title is required." });
        return;
      }
      if (!body.trim()) {
        setSnackbar({ open: true, severity: "warning", message: "Body is required." });
        return;
      }
      const payload = {
        title: title.trim(),
        body: body.trim(),
      };
      const dataObj = parseData();
      if (Object.keys(dataObj).length > 0) payload.data = dataObj;

      if (targetType === "single") {
        if (!selectedUser?.id) {
          setSnackbar({ open: true, severity: "warning", message: "Select one user." });
          return;
        }
        payload.userId = selectedUser.id;
      } else if (targetType === "multiple") {
        const ids = selectedUsers.map((u) => u.id).filter(Boolean);
        if (ids.length === 0) {
          setSnackbar({ open: true, severity: "warning", message: "Select at least one user." });
          return;
        }
        payload.userIds = ids;
      } else {
        payload.target = "all";
      }

      setSending(true);
      const response = await apiClient.post("sharing/admin/notify", payload);
      const counts = response?.data?.counts || { requested: 0, sent: 0, failed: 0 };
      setResult(counts);
      setSnackbar({
        open: true,
        severity: "success",
        message: `Notification sent. Success: ${counts.sent}, Failed: ${counts.failed}`,
      });
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to send notification.";
      setSnackbar({ open: true, severity: "error", message: msg });
    } finally {
      setSending(false);
    }
  };

  const applyTemplate = (templateKey) => {
    const template = QUICK_TEMPLATES.find((t) => t.key === templateKey);
    if (!template) return;
    setActiveTemplate(template.key);
    setTitle(template.title);
    setBody(template.body);
    setExtraData(JSON.stringify(template.data, null, 2));
  };

  return (
    <PageLayout
      title="Notifications"
      subtitle="Send push notifications to one user, selected users, or all users."
    >
      <Paper
        elevation={0}
        sx={{ p: 3, borderRadius: 2, border: "1px solid", borderColor: "divider" }}
      >
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Quick templates
            </Typography>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {QUICK_TEMPLATES.map((t) => (
                <Chip
                  key={t.key}
                  label={t.label}
                  clickable
                  color={activeTemplate === t.key ? "primary" : "default"}
                  variant={activeTemplate === t.key ? "filled" : "outlined"}
                  onClick={() => applyTemplate(t.key)}
                />
              ))}
            </Stack>
          </Box>

          <FormControl>
            <FormLabel>Target</FormLabel>
            <RadioGroup
              row
              value={targetType}
              onChange={(e) => setTargetType(e.target.value)}
            >
              <FormControlLabel value="single" control={<Radio />} label="Single user" />
              <FormControlLabel value="multiple" control={<Radio />} label="Multiple users" />
              <FormControlLabel value="all" control={<Radio />} label="All users" />
            </RadioGroup>
          </FormControl>

          {loadingUsers ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                Loading users...
              </Typography>
            </Box>
          ) : (
            <>
              {targetType === "single" && (
                <Autocomplete
                  options={userOptions}
                  value={selectedUser}
                  onChange={(_, v) => setSelectedUser(v)}
                  renderInput={(params) => <TextField {...params} label="Select user" />}
                />
              )}
              {targetType === "multiple" && (
                <Autocomplete
                  multiple
                  options={userOptions}
                  value={selectedUsers}
                  onChange={(_, v) => setSelectedUsers(v)}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip {...getTagProps({ index })} key={option.id} label={option.label} size="small" />
                    ))
                  }
                  renderInput={(params) => <TextField {...params} label="Select users" />}
                />
              )}
            </>
          )}

          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
          />
          <TextField
            label="Body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            fullWidth
            multiline
            minRows={3}
          />
          <TextField
            label="Data (optional JSON)"
            placeholder='{"type":"admin_custom","screen":"dashboard"}'
            value={extraData}
            onChange={(e) => setExtraData(e.target.value)}
            fullWidth
            multiline
            minRows={3}
          />

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              onClick={handleSend}
              disabled={sending}
            >
              {sending ? "Sending..." : "Send Notification"}
            </Button>
            {result && (
              <Chip
                icon={<NotificationsActive />}
                color="primary"
                variant="outlined"
                label={`Requested ${result.requested} · Sent ${result.sent} · Failed ${result.failed}`}
              />
            )}
          </Box>
        </Stack>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4500}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageLayout>
  );
};

export default Notifications;
